import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOverlayFocusReturn } from './use-overlay-focus-return';
import { hasPrimitiveDescription } from './utils';

/**
 * Sober DS (v2) — Dialog (Modal). Radix Dialog wrapper, sober-* tokens only.
 * Radix handles focus trap, Escape and overlay click-to-close. Single 1px
 * outline (no colored borders/accents), surface bg with the raised shadow.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(28,29,33,0.45)]',
      'so-overlay-motion data-[state=open]:animate-in data-[state=closed]:animate-out duration-200 ease-out motion-reduce:animate-none',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * Larguras da janela (padrão 2026-09-04):
 *  sm 420 — confirmações e formulários de um campo
 *  md 560 — formulários comuns
 *  lg 760 — formulários em duas colunas, assistentes
 *  xl 960 — quadros largos (pipeline, pré-visualização de mídia)
 */
const dialogSize = {
  sm: 'max-w-[420px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[760px]',
  xl: 'max-w-[960px]',
} as const;

/**
 * Gutter lateral mínimo do Dialog (RC-005): 16px por lado em QUALQUER viewport.
 * A folga vive em estilo inline (não em classe) de propósito: o tailwind-merge
 * do `cn` deixa o `className` do consumidor substituir utilitários de largura
 * (`max-w-*`/`w-*`), mas estilo inline vence classes na cascata — a folga
 * prevalece sempre, enquanto o teto (`max-w` do size) segue sobrescrevível.
 * Largura efetiva = min(calc(100vw - 32px), max-width) → desktop inalterado.
 */
const DIALOG_GUTTER_WIDTH = 'calc(100vw - 32px)';

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  size?: keyof typeof dialogSize;
  showClose?: boolean;
  overlayClassName?: string;
}

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size = 'md', style, showClose = true, overlayClassName, ...props }, ref) => {
  const focusReturn = useOverlayFocusReturn(props);
  const hasDescription = hasPrimitiveDescription(children, 'DialogDescription');
  // Radix EXIGE um título para o leitor de tela anunciar a janela; sem ele, avisa em runtime
  // e quem usa leitor ouve só "diálogo". Mesmo tratamento já dado à descrição: o primitivo
  // garante o piso, e cada janela continua devendo o seu título de verdade.
  const hasTitle = hasPrimitiveDescription(children, 'DialogTitle');

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 flex -translate-x-1/2 -translate-y-1/2 flex-col',
          'max-h-[90dvh] overflow-hidden rounded-so-xl border border-sober-outline bg-sober-surface shadow-so-raised',
          'so-overlay-motion data-[state=open]:animate-in data-[state=closed]:animate-out duration-200 ease-out motion-reduce:animate-none',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          dialogSize[size],
          className
        )}
        // A folga entra DEPOIS do style do consumidor: nem style custom anula o gutter.
        style={{ ...style, width: DIALOG_GUTTER_WIDTH }}
        {...props}
        {...focusReturn}
      >
        {!hasTitle && (
          <DialogPrimitive.Title className="sr-only">Janela de diálogo</DialogPrimitive.Title>
        )}
        {!hasDescription && (
          <DialogPrimitive.Description className="sr-only">
            Janela de diálogo do ValiosasCRM.
          </DialogPrimitive.Description>
        )}
        {children}
        {showClose && <DialogPrimitive.Close
          className={cn(
            // 44px é o mínimo de alvo de toque (WCAG 2.5.5). Era 32px: no celular, fechar
            // um modal exigia mira. A Story 39.8 tinha consertado isto e o PR foi fechado
            // sem merge — só o gutter voltou (44.11). O ícone tem 16px (proporcional ao
            // título de 16px); o que é grande é a área tocável. O cabeçalho reserva esta
            // faixa com pr-14, então nenhum texto passa por baixo do X.
            'absolute right-3 top-3 grid h-11 w-11 place-items-center rounded-so-sm',
            'text-sober-secondary transition-colors hover:bg-sober-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg',
            'disabled:pointer-events-none'
          )}
        >
          <X className="size-4" />
          <span className="sr-only">Fechar</span>
        </DialogPrimitive.Close>}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

/**
 * Cabeçalho da janela — padrão 2026-09-04.
 * Uma COLUNA: título em cima, descrição embaixo (antes era uma linha flex e os dois
 * ficavam lado a lado — título estreito quebrando em três linhas, descrição por baixo
 * do X). Margem lateral igual à do corpo (24px) e faixa direita reservada para o X
 * (pr-14 = 56px). Com um <DialogIcon> como filho, vira duas colunas: o ícone ocupa as
 * duas linhas à esquerda e o título/descrição ficam à direita.
 */
export const DialogHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const hasIcon = hasPrimitiveDescription(children, 'DialogIcon');
    return (
      <div
        ref={ref}
        className={cn(
          'grid gap-y-1 border-b border-sober-outline px-6 pb-4 pr-14 pt-5',
          hasIcon
            ? 'grid-cols-[auto_minmax(0,1fr)] gap-x-3 [&>h2]:mt-1.5'
            : 'grid-cols-1',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
DialogHeader.displayName = 'DialogHeader';

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('min-w-0 break-words text-so-16 font-bold text-sober-text', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('min-w-0 text-so-14 text-sober-secondary', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

const dialogIconTone = {
  neutral: 'bg-sober-muted text-sober-secondary',
  primary: 'bg-sober-primary-bg text-sober-primary',
  danger: 'bg-sober-red-bg text-sober-red',
  success: 'bg-sober-green-bg text-sober-green-fg',
} as const;

export interface DialogIconProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: keyof typeof dialogIconTone;
}

/**
 * Ícone do cabeçalho: ladrilho de 36px com ícone de 20px, tinta por tom (cor = tint,
 * regra Sober). Único lugar para ícone na janela — nunca dentro do título. Use quando
 * a ação pede sinal (destrutiva = danger, concluída = success, envio = primary).
 */
export const DialogIcon = React.forwardRef<HTMLSpanElement, DialogIconProps>(
  ({ className, tone = 'neutral', ...props }, ref) => (
    <span
      ref={ref}
      data-dialog-icon=""
      aria-hidden="true"
      className={cn(
        'row-span-2 grid size-9 shrink-0 self-start place-items-center rounded-so-sm [&_svg]:size-5',
        dialogIconTone[tone],
        className
      )}
      {...props}
    />
  )
);
DialogIcon.displayName = 'DialogIcon';

export const DialogBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5', className)}
      {...props}
    />
  )
);
DialogBody.displayName = 'DialogBody';

export const DialogFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex max-w-full flex-wrap items-center justify-end gap-2 border-t border-sober-outline px-6 py-4',
        className
      )}
      {...props}
    />
  )
);
DialogFooter.displayName = 'DialogFooter';
